import {
  createCommandAsyncThunk,
  createQueryAsyncThunk,
} from "../../../app/createApiAsyncThunk";
import {
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import {
  changeRecruitmentArea,
  createRecruitmentArea,
  recruitmentAreaById,
  recruitmentAreas,
  removeRecruitmentAreaCommand,
} from "./recruitmentsAPI";
import { RootState } from "../../../app/store";
import { Nullable } from "../../../common/types/nullable";
import {
  ChangeRecruitmentArea,
  CreateRecruitmentArea,
  RecruitmentArea,
  RecruitmentAreaById,
  RecruitmentAreaChanged,
  RecruitmentAreaCreated,
  RecruitmentAreaDetails,
  RecruitmentAreaRemoved,
  RemoveRecruitmentArea,
} from "./types";
import { isRecruitmentAreaUpdated } from "./helpers";
import isEmpty from "../../../common/helpers/isEmpty";

type status = "idle" | "pending" | "succeeded" | "failed";

export type SliceState = {
  recruitmentAreas: {
    entities: Record<string, RecruitmentArea>;
    ids: string[];
    status: status;
  };
  recruitmentAreaDetails: {
    data: Nullable<RecruitmentAreaDetails>;
    status: status;
  };
  // add more state slices here if needed
};

const recruitmentAreasAdapter = createEntityAdapter<RecruitmentArea>({
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

const initialRecruitmentAreas = recruitmentAreasAdapter.getInitialState({
  status: "idle" as const,
});
const initialRecruitmentAreaDetails = {
  data: null,
  status: "idle" as const,
};

const initialState: SliceState = {
  recruitmentAreas: initialRecruitmentAreas,
  recruitmentAreaDetails: initialRecruitmentAreaDetails,
};

export const fetchRecruitmentAreas = createQueryAsyncThunk<
  RecruitmentArea[],
  { expertId?: string }
>("recruitments/fetchRecruitmentAreas", recruitmentAreas);

export const fetchRecruitmentAreaById = createQueryAsyncThunk<
  RecruitmentAreaDetails,
  RecruitmentAreaById
>("recruitments/fetchRecruitmentAreaById", recruitmentAreaById);

export const createNewRecruitmentArea = createCommandAsyncThunk<
  CreateRecruitmentArea,
  RecruitmentAreaCreated
>("recruitments/createNewRecruitmentArea", createRecruitmentArea);

export const removeRecruitmentArea = createCommandAsyncThunk<
  RemoveRecruitmentArea,
  RecruitmentAreaRemoved
>("recruitments/removeRecruitmentArea", removeRecruitmentAreaCommand);

export const updateRecruitmentArea = createCommandAsyncThunk<
  ChangeRecruitmentArea,
  RecruitmentAreaChanged
>("recruitments/updateRecruitmentArea", changeRecruitmentArea);

export const recruitmentsSlice = createSlice({
  name: "recruitments",
  initialState,
  reducers: {
    resetRecruitmentAreaDetails: (state) => {
      state.recruitmentAreaDetails = initialRecruitmentAreaDetails;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchRecruitmentAreas.fulfilled, (state, action) => {
      recruitmentAreasAdapter.setAll(state.recruitmentAreas, action.payload);
      state.recruitmentAreas.status = "succeeded";
    });
    builder.addCase(fetchRecruitmentAreas.pending, (state) => {
      state.recruitmentAreas.status = "pending";
    });
    builder.addCase(fetchRecruitmentAreas.rejected, (state) => {
      state.recruitmentAreas.status = "failed";
    });
    builder.addCase(fetchRecruitmentAreaById.fulfilled, (state, action) => {
      state.recruitmentAreaDetails.data = action.payload;
      state.recruitmentAreaDetails.status = "succeeded";
    });
    builder.addCase(fetchRecruitmentAreaById.pending, (state) => {
      state.recruitmentAreaDetails.status = "pending";
    });
    builder.addCase(fetchRecruitmentAreaById.rejected, (state) => {
      state.recruitmentAreaDetails.status = "failed";
    });
    builder.addCase(createNewRecruitmentArea.fulfilled, (state, action) => {
      if (action.payload && !isEmpty(action.payload)) {
        const recruitmentAreaCreated = action.payload[0];
        const newRecruitmentArea: RecruitmentArea = {
          id: recruitmentAreaCreated.id,
          name: recruitmentAreaCreated.payload.name,
          isTeamArea: false,
        };
        recruitmentAreasAdapter.addOne(
          state.recruitmentAreas,
          newRecruitmentArea,
        );
      }
      state.recruitmentAreas.status = "succeeded";
    });
    builder.addCase(createNewRecruitmentArea.pending, (state) => {
      state.recruitmentAreas.status = "pending";
    });
    builder.addCase(createNewRecruitmentArea.rejected, (state) => {
      state.recruitmentAreas.status = "failed";
    });
    builder.addCase(removeRecruitmentArea.fulfilled, (state, action) => {
      if (action.payload && !isEmpty(action.payload)) {
        recruitmentAreasAdapter.removeOne(
          state.recruitmentAreas,
          action.payload[0].id,
        );
      }
      state.recruitmentAreas.status = "succeeded";
    });
    builder.addCase(removeRecruitmentArea.pending, (state) => {
      state.recruitmentAreaDetails.status = "pending";
    });
    builder.addCase(removeRecruitmentArea.rejected, (state) => {
      state.recruitmentAreaDetails.status = "failed";
    });
    builder.addCase(updateRecruitmentArea.fulfilled, (state, action) => {
      if (action.payload) {
        if (!state.recruitmentAreaDetails.data) {
          return;
        }

        action.payload.forEach((event) => {
          if (event.name === "rcrmntAreaChg") {
            if (isRecruitmentAreaUpdated(event.payload)) {
              recruitmentAreasAdapter.updateOne(state.recruitmentAreas, {
                id: event.id,
                changes: { name: event.payload.name },
              });

              // fetchRecruitmentAreaById will update other details
            }
          }
        });
      }
      state.recruitmentAreaDetails.status = "succeeded";
    });
    builder.addCase(updateRecruitmentArea.pending, (state) => {
      state.recruitmentAreaDetails.status = "pending";
    });
    builder.addCase(updateRecruitmentArea.rejected, (state) => {
      state.recruitmentAreaDetails.status = "failed";
    });
  },
});

export const { selectAll: selectAllRecruitmentAreas } =
  recruitmentAreasAdapter.getSelectors(
    (state: RootState) => state.recruitments.recruitmentAreas,
  );

export const selectRecruitmentAreaOptions = createSelector(
  [selectAllRecruitmentAreas],
  (recruitmentAreas) =>
    recruitmentAreas
      ? recruitmentAreas.map(({ id, name, isTeamArea, zipCodes }) => ({
          id,
          name,
          isTeamArea,
          zipCodes: zipCodes || [],
        }))
      : [],
);

export const selectTeamRecruitmentArea = createSelector(
  [selectAllRecruitmentAreas],
  (recruitmentAreas) =>
    recruitmentAreas
      ? recruitmentAreas.filter((recruitmentArea) => recruitmentArea.isTeamArea)
      : [],
);

export const { resetRecruitmentAreaDetails } = recruitmentsSlice.actions;
export const recruitmentsReducer = recruitmentsSlice.reducer;
